import { Box, Flex, Icon, useBreakpointValue } from '@chakra-ui/react';
import { MdPerson as ProfileIcon } from 'react-icons/md';
import HomeSearchBar from '@/components/HomeSearchBar';
import FilterSheet from '@/components/FilterSheet';
import AddFilterButton from '@/components/AddFilterButton';
import { useScrollVisibility } from '@/hooks/useScrollVisibility';

interface Props {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  placeholder?: string;
  showFilter?: boolean;
  selectedTags?: string[];
  onToggleTag?: (tag: string, group: string) => void;
  selectedRatings?: number[];
  onToggleRating?: (value: number) => void;
  onClearFilters?: () => void;
  totalActiveFilters?: number;
  filterTrigger?: number;
  onFilterClick?: () => void;
  onProfileClick: () => void;
}

export default function BrowseHeader({
  searchTerm,
  onSearchChange,
  placeholder,
  showFilter = true,
  selectedTags = [],
  onToggleTag = () => {},
  selectedRatings = [],
  onToggleRating = () => {},
  onClearFilters = () => {},
  totalActiveFilters = 0,
  filterTrigger = 0,
  onFilterClick,
  onProfileClick,
}: Props) {
  const isMobile = useBreakpointValue({ base: true, lg: false });
  // Mobile: hide on scroll-down, reveal immediately on scroll-up.
  // Desktop: hook disabled → always visible → stays pinned at the top.
  const { isVisible } = useScrollVisibility({ enabled: !!isMobile });

  return (
    <>
      {/* Sticky reveal wrapper — holds only the visible header rows.
          FilterSheet stays a sibling outside this box: a `transform` here
          would re-base any non-portaled position:fixed descendants. */}
      <Box
        bg="bg.canvas"
        position="sticky"
        top={0}
        zIndex={10}
        transform={isVisible ? 'translateY(0)' : 'translateY(-100%)'}
        transition="transform 0.3s ease-in-out"
      >
        {/* Desktop: search bar + Add filter on same row */}
        <Flex display={{ base: 'none', lg: 'flex' }} align="center" gap={3} px={4} pt={6} pb={2}>
          <Box flex={1}>
            <HomeSearchBar
              noPx
              value={searchTerm}
              onChange={onSearchChange}
              placeholder={placeholder}
              onClear={showFilter ? onClearFilters : undefined}
            />
          </Box>
          {showFilter && (
            <AddFilterButton
              selectedTags={selectedTags}
              onToggleTag={onToggleTag}
              selectedRatings={selectedRatings}
              onToggleRating={onToggleRating}
            />
          )}
        </Flex>

        {/* Mobile: logo + search bar + profile icon */}
        <Flex
          display={{ base: 'flex', lg: 'none' }}
          align="center"
          gap={2}
          px={4}
          pb={2}
          style={{ paddingTop: 'max(env(safe-area-inset-top), 16px)' }}
        >
          <img
            src="/logo.png"
            alt="Byte Pantry"
            style={{ width: '26px', height: '26px', objectFit: 'contain', flexShrink: 0 }}
          />
          <Box flex={1}>
            <HomeSearchBar
              noPx
              value={searchTerm}
              onChange={onSearchChange}
              onFilterClick={showFilter ? onFilterClick : undefined}
              totalActive={totalActiveFilters}
              placeholder={placeholder}
              onClear={showFilter ? onClearFilters : undefined}
            />
          </Box>
          <Box
            as="button"
            aria-label="Profile & Household"
            onClick={onProfileClick}
            w="40px"
            h="40px"
            borderRadius="full"
            bg="bg.surface"
            border="1.5px solid"
            borderColor="border.muted"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            cursor="pointer"
            transition="background 0.12s"
            _hover={{ bg: 'bg.muted' }}
          >
            <Icon as={ProfileIcon} boxSize="17px" color="text.muted" />
          </Box>
        </Flex>
      </Box>

      {showFilter && (
        <FilterSheet
          selectedTags={selectedTags}
          onToggleTag={onToggleTag}
          selectedRatings={selectedRatings}
          onToggleRating={onToggleRating}
          onClear={onClearFilters}
          totalActive={totalActiveFilters}
          triggerAddFilter={filterTrigger}
        />
      )}
    </>
  );
}
